import { useLazyQuery } from '@apollo/client';
import { GET_TMS_ROUTES_FOR_SEARCH_SELECT, GetTmsRoutesForSearchSelectResponse } from '../route/graphql/queries/getTmsRoutes';
import { GET_TMS_SCHEDULES_FOR_SEARCH_SELECT, GetTmsSchedulesForSearchSelectResponse } from '../schedule/graphql/queries/getTmsSchedules';
import { GET_TMS_ROUTE_WITH_CARRIER_INFO_BY_ID, GetTmsRouteWithCarrierInfoByIdResponse } from '../route/graphql/queries/getTmsRoutes';
import { useCallback } from 'react';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { WaypointTypeEnum } from '../../../enums/TmsEnum';

export type SearchSelectCallback = (data: { value: string | number; label: string }[]) => void;

export type RouteSearchResult = {
  id: string;
  routeName: string;
  routeType?: number;
  startWarehouseId: number;
  carrierId: number;
  businessType?: number;
  carrierSourceType?: number | null;
  carrierName?: string | null;
};

/**
 * TMS搜索相关的自定义Hook
 * 提供Route和Schedule的搜索功能
 */
export const useTmsSearch = () => {
  const { currentWarehouse } = useWarehouseContext();
  const [searchRoutes] = useLazyQuery<GetTmsRoutesForSearchSelectResponse>(GET_TMS_ROUTES_FOR_SEARCH_SELECT);
  const [searchSchedules] = useLazyQuery<GetTmsSchedulesForSearchSelectResponse>(GET_TMS_SCHEDULES_FOR_SEARCH_SELECT);
  const [getRouteWithCarrierInfoByIdQuery] = useLazyQuery<GetTmsRouteWithCarrierInfoByIdResponse>(GET_TMS_ROUTE_WITH_CARRIER_INFO_BY_ID);

  /**
   * 搜索Route
   * @param searchValue
   * @param callback
   * @param status
   * @param deduplicateByRouteName
   * @param routeType
   * @param waypointTypes 途经点类型列表：起点(Ship From Here) 用 [WaypointTypeEnum.Origin]，途经点/终点(Ship To Here) 用 [WaypointTypeEnum.Waypoint, WaypointTypeEnum.Destination]，默认 [WaypointTypeEnum.Origin]
   * @param businessType 路线业务类型(业务来源)
   */
  const handleRouteSearch = (
    searchValue: string,
    callback: SearchSelectCallback,
    status?: number | null,
    deduplicateByRouteName: boolean = false,
    routeType?: number | null,
    waypointTypes: number[] = [WaypointTypeEnum.Origin],
    businessType?: number | null
  ) => {
    if (!searchValue || searchValue.trim() === '') {
      callback([]);
      return;
    }

    const currentWarehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : null;
    if (!currentWarehouseId || waypointTypes.length === 0) {
      callback([]);
      return;
    }

    const variables: {
      routeName: string;
      warehouseId: number;
      waypointTypes: number[];
      status?: number;
      routeType?: number;
      businessType?: number;
    } = {
      routeName: searchValue,
      warehouseId: currentWarehouseId,
      waypointTypes
    };
    if (status !== null && status !== undefined) {
      variables.status = status;
    }
    if (routeType !== null && routeType !== undefined) {
      variables.routeType = routeType;
    }
    if (businessType !== null && businessType !== undefined) {
      variables.businessType = businessType;
    }

    searchRoutes({
      variables
    }).then(result => {
      const routes = result.data?.getTmsRoutesForSearchSelect;
      if (routes) {
        const filtered = deduplicateByRouteName
          ? routes
              .filter((route, index, self) => {
                const routeName = route.routeName || '';
                return routeName && self.findIndex(r => (r.routeName || '') === routeName) === index;
              })
              .map(route => ({ value: route.routeName || '', label: route.routeName || '' }))
          : routes.map(route => ({
              value: typeof route.id === 'string' ? parseInt(route.id) : route.id,
              label: route.routeName || '',
              subLabel: route.carrierName || undefined
            }));
        callback(filtered);
      } else {
        callback([]);
      }
    }).catch(() => {
      callback([]);
    });
  };

  /**
   * 根据 routeId 获取 route 数据
   */
  const getRouteWithCarrierInfoById = useCallback(async (routeId: number | string | undefined): Promise<RouteSearchResult | null> => {
    if (!routeId) return null;
    
    const routeIdNum = typeof routeId === 'string' ? parseInt(routeId) : routeId;
    if (isNaN(routeIdNum)) return null;
    try {
      const result = await getRouteWithCarrierInfoByIdQuery({
        variables: { id: routeIdNum.toString() }
      });
      
      if (result.data?.getTmsRouteWithCarrierInfoById) {
        const route = result.data.getTmsRouteWithCarrierInfoById;
        const routeData: RouteSearchResult = {
          id: route.id.toString(),
          routeName: route.routeName || '',
          routeType: route.routeType ? Number(route.routeType) : undefined,
          startWarehouseId: route.startWarehouseId,
          carrierId: route.carrierId,
          businessType: route.businessType,
          carrierSourceType: route.carrierSourceType ?? null,
          carrierName: route.carrierName ?? null
        };
        return routeData;
      }
    } catch (error) {
      console.error('Failed to get route by id:', error);
    }
    
    return null;
  }, [getRouteWithCarrierInfoByIdQuery]);

  /**
   * 搜索Schedule
   * 根据当前选中的 warehouse 筛选 schedule（只显示 warehouseId 匹配的 schedule）
   */
  const handleScheduleSearch = (searchValue: string, callback: SearchSelectCallback,  routeType?: number | null) => {
    if (!searchValue || searchValue.trim() === '') {
      callback([]);
      return;
    }

    // 获取当前选中的 warehouse ID
    const currentWarehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : null;

    // routeType
    const currentRouteType = (routeType !== null && routeType !== undefined) ? routeType : null;

    searchSchedules({
      variables: {
        scheduleName: searchValue,
        warehouseId: currentWarehouseId,
        routeType: currentRouteType
      }
    }).then(result => {
      if (result.data?.getAllTmsSchedules) {
        const filtered = result.data.getAllTmsSchedules.map(schedule => ({
          value: typeof schedule.id === 'string' ? schedule.id : String(schedule.id),
          label: schedule.scheduleName || ''
        }));
        callback(filtered);
      } else {
        callback([]);
      }
    }).catch(() => {
      callback([]);
    });
  };

  return {
    handleRouteSearch,
    handleScheduleSearch,
    getRouteWithCarrierInfoById
  };
};

